import type { WorldPoint } from './canvas';
import {
  pointsFor,
  type GeometryModel,
  type GeometryOwner,
} from './model';

export type GeometryTool =
  | 'select'
  | 'pan'
  | 'draw-roof-rectangle'
  | 'draw-roof-polygon'
  | 'draw-opening-rectangle'
  | 'draw-opening-polygon'
  | 'insert-vertex'
  | 'move-opening';

export type GeometrySelection =
  | { kind: 'roof' }
  | { kind: 'opening'; openingId: string }
  | { kind: 'vertex'; owner: GeometryOwner; pointId: string }
  | { kind: 'segment'; owner: GeometryOwner; segmentIndex: number };

export type GeometryDraft =
  | {
      kind: 'polygon';
      owner: 'roof' | 'opening';
      points: WorldPoint[];
      cursor?: WorldPoint;
    }
  | {
      kind: 'rectangle';
      owner: 'roof' | 'opening';
      start: WorldPoint;
      end: WorldPoint;
    };

export interface SelectionBounds {
  min: WorldPoint;
  max: WorldPoint;
}

function distanceToSegment(point: WorldPoint, start: WorldPoint, end: WorldPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(point.x - start.x, point.y - start.y);
  const projection = Math.max(0, Math.min(1,
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
  ));
  return Math.hypot(
    point.x - (start.x + projection * dx),
    point.y - (start.y + projection * dy),
  );
}

export function projectPointToSegment(point: WorldPoint, start: WorldPoint, end: WorldPoint): WorldPoint {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return { ...start };
  const projection = Math.max(0, Math.min(1,
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
  ));
  return {
    x: start.x + projection * dx,
    y: start.y + projection * dy,
  };
}

export function pointInPolygon(point: WorldPoint, polygon: readonly WorldPoint[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const a = polygon[i];
    const b = polygon[j];
    if (!a || !b) continue;
    const intersects = ((a.y > point.y) !== (b.y > point.y))
      && point.x < ((b.x - a.x) * (point.y - a.y)) / ((b.y - a.y) || Number.EPSILON) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function closestVertex(
  model: GeometryModel,
  world: WorldPoint,
  radius: number,
): GeometrySelection | undefined {
  let bestDistance = radius;
  let best: GeometrySelection | undefined;

  for (const candidate of model.points) {
    const distance = Math.hypot(candidate.x - world.x, candidate.y - world.y);
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = { kind: 'vertex', owner: { kind: 'roof' }, pointId: candidate.id };
    }
  }

  for (const opening of model.openings) {
    for (const candidate of opening.points) {
      const distance = Math.hypot(candidate.x - world.x, candidate.y - world.y);
      if (distance <= bestDistance) {
        bestDistance = distance;
        best = {
          kind: 'vertex',
          owner: { kind: 'opening', openingId: opening.id },
          pointId: candidate.id,
        };
      }
    }
  }

  return best;
}

function closestSegment(
  model: GeometryModel,
  world: WorldPoint,
  radius: number,
): GeometrySelection | undefined {
  let bestDistance = radius;
  let best: GeometrySelection | undefined;

  const inspect = (owner: GeometryOwner, closed: boolean) => {
    const points = pointsFor(model, owner);
    const segmentCount = closed ? points.length : Math.max(0, points.length - 1);
    for (let index = 0; index < segmentCount; index += 1) {
      const start = points[index];
      const end = points[(index + 1) % points.length];
      if (!start || !end) continue;
      const distance = distanceToSegment(world, start, end);
      if (distance <= bestDistance) {
        bestDistance = distance;
        best = { kind: 'segment', owner, segmentIndex: index };
      }
    }
  };

  inspect({ kind: 'roof' }, model.closed);
  for (const opening of model.openings) inspect({ kind: 'opening', openingId: opening.id }, true);
  return best;
}

/** Hit priority: vertex > edge > opening body > roof body > empty canvas. */
export function hitTest(
  model: GeometryModel,
  world: WorldPoint,
  radius: number,
): GeometrySelection | undefined {
  const vertex = closestVertex(model, world, radius);
  if (vertex) return vertex;

  const segment = closestSegment(model, world, radius);
  if (segment) return segment;

  for (let index = model.openings.length - 1; index >= 0; index -= 1) {
    const opening = model.openings[index];
    if (opening && pointInPolygon(world, opening.points)) {
      return { kind: 'opening', openingId: opening.id };
    }
  }

  if (model.closed && pointInPolygon(world, model.points)) return { kind: 'roof' };
  return undefined;
}

export function ownerFromSelection(selection: GeometrySelection): GeometryOwner | undefined {
  if (selection.kind === 'vertex' || selection.kind === 'segment') return selection.owner;
  if (selection.kind === 'opening') return { kind: 'opening', openingId: selection.openingId };
  if (selection.kind === 'roof') return { kind: 'roof' };
  return undefined;
}

export function selectionBounds(
  model: GeometryModel,
  selection: GeometrySelection | undefined,
): SelectionBounds | undefined {
  if (!selection) return undefined;
  let points: readonly WorldPoint[] = [];

  if (selection.kind === 'roof') points = model.points;
  if (selection.kind === 'opening') {
    points = model.openings.find((opening) => opening.id === selection.openingId)?.points ?? [];
  }
  if (selection.kind === 'vertex') {
    const candidate = pointsFor(model, selection.owner).find((point) => point.id === selection.pointId);
    if (candidate) points = [candidate];
  }
  if (selection.kind === 'segment') {
    const ring = pointsFor(model, selection.owner);
    const start = ring[selection.segmentIndex];
    const end = ring[(selection.segmentIndex + 1) % ring.length];
    if (start && end) points = [start, end];
  }

  if (points.length === 0) return undefined;
  return {
    min: {
      x: Math.min(...points.map((point) => point.x)),
      y: Math.min(...points.map((point) => point.y)),
    },
    max: {
      x: Math.max(...points.map((point) => point.x)),
      y: Math.max(...points.map((point) => point.y)),
    },
  };
}

export function selectionForObjectId(
  model: GeometryModel,
  objectId: string,
): GeometrySelection | undefined {
  const roofPoint = model.points.find((point) => point.id === objectId);
  if (roofPoint) return { kind: 'vertex', owner: { kind: 'roof' }, pointId: roofPoint.id };

  for (const opening of model.openings) {
    if (opening.id === objectId) return { kind: 'opening', openingId: opening.id };
    const openingPoint = opening.points.find((point) => point.id === objectId);
    if (openingPoint) {
      return {
        kind: 'vertex',
        owner: { kind: 'opening', openingId: opening.id },
        pointId: openingPoint.id,
      };
    }
  }

  if (objectId === 'outline' || objectId === 'roof') return { kind: 'roof' };
  return undefined;
}
